import styled from "styled-components";
import { useEffect, useRef } from "react";
import { CalendarDays } from "lucide-react";

import { theme } from "./theme";
import { formatDate } from "./dateFormat";
import { useT } from "./i18n";

// A single labelled date field that matches the Beranda date control (DateNav's DateButton): a calendar
// icon + the formatted date, backed by the native date picker. Nullable — shows the placeholder when
// empty. Used for the shift-notes from/to range so both pages share one look.
const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${theme.muted};
`;

const Button = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 160px;
  justify-content: flex-start;
  padding: 0.4rem 0.8rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${(props) => (props.$empty ? theme.muted : theme.ink)};
  cursor: pointer;
  &:hover {
    border-color: ${theme.gold};
  }
  &:disabled {
    background: ${theme.canvas};
    cursor: not-allowed;
    opacity: 0.7;
  }
  &:disabled:hover {
    border-color: ${theme.border};
  }
`;

// Present (so showPicker works) but invisible and non-interactive; the button drives it.
const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
`;

const DatePickField = (props) => {
  const { lang } = useT();
  const inputRef = useRef(null);
  // Hoisted so the effects depend on the values they use, not on the whole props object.
  const selected = props.value;
  const onDateChange = props.onChange;

  /* Deliberately UNCONTROLLED, and committed on the native `change` event only.
   *
   * React's onChange maps to the DOM `input` event, which Chrome fires on a date input while the
   * operator is still walking months inside the picker. Every arrow click would report a new date
   * to the parent — resetting a form, re-running a preview, or navigating the page. `change` fires
   * once, when a date is actually chosen.
   */
  useEffect(() => {
    const el = inputRef.current;
    if (el && el.value !== (selected || "")) {
      el.value = selected || "";
    }
  }, [selected]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return undefined;

    const commit = () => {
      if (el.value !== (selected || "")) onDateChange(el.value);
    };
    el.addEventListener("change", commit);
    return () => el.removeEventListener("change", commit);
  }, [selected, onDateChange]);

  const openPicker = () => {
    if (props.disabled) return;

    const el = inputRef.current;
    if (el && typeof el.showPicker === "function") {
      el.showPicker();
    } else if (el) {
      el.focus();
    }
  };

  return (
    <Field>
      {props.label}
      <Button
        type="button"
        $empty={!props.value}
        disabled={props.disabled}
        onClick={openPicker}
      >
        <CalendarDays size={15} />
        {props.value ? formatDate(props.value, lang) : props.placeholder || "—"}
        <HiddenInput
          ref={inputRef}
          type="date"
          defaultValue={props.value || ""}
          min={props.min || undefined}
          max={props.max || undefined}
        />
      </Button>
    </Field>
  );
};

export default DatePickField;
